import { ApolloClient, DocumentNode } from '@apollo/client';
import { DisplayDateRange } from 'components/Charting/types';
import { queryDataToD3Data } from 'hooks/useChartDataFetcher/reshape-data';
import { downloadExcelExport } from 'pages/Optimisation/components/ContractedVolume/excel-export';
import { MarketKey } from 'pages/Optimisation/components/ContractedVolume/types';
import {
  GET_ANCILLARY_TRADES_DOWNLOAD,
  GET_BALANCING_MECHANISM_TRADES_DOWNLOAD,
  GET_WHOLESALE_TRADES_DOWNLOAD,
} from 'pages/Optimisation/queries';
import { ApiName } from 'types/apiName';
import { IChartTrade, TRADE_TYPE } from 'types/trade';

const DEFAULT_QUERY_SIZE = 500;

const fetchTrades = async (
  client: ApolloClient,
  query: DocumentNode,
  defaultQueryOptions: { variables: {} },
  marketKey: MarketKey,
) => {
  let cursor = null;
  let hasNextPage = true;
  let trades: IChartTrade[] = [];

  while (hasNextPage) {
    const result: any = await client.query({
      fetchPolicy: 'no-cache',
      query,
      variables: {
        ...defaultQueryOptions.variables,
        paging: {
          first: DEFAULT_QUERY_SIZE,
          after: cursor,
        },
      },
      context: {
        apiName: ApiName.plutoApi,
      },
    });

    cursor = result?.data.site[`${marketKey}Trades`].pageInfo.endCursor;
    hasNextPage =
      result?.data.site[`${marketKey}Trades`].pageInfo.hasNextPage ?? false;

    trades = trades.concat(
      result ? queryDataToD3Data(marketKey, result.data.site, false) : [],
    );
  }

  return trades;
};

export const downloadData = async (
  client: ApolloClient,
  {
    siteId,
    siteName,
    dateRange,
    tradeTypes,
  }: {
    siteId?: string;
    siteName?: string;
    dateRange: DisplayDateRange;
    tradeTypes: TRADE_TYPE[];
  },
): Promise<boolean> => {
  if (!siteId) return false;
  const defaultQueryOptions = {
    variables: {
      filter: {
        // Include any trade that *starts* in the date range
        // even if it finishes outside the range.
        and: [
          {
            start: { gte: dateRange.start },
          },
          {
            start: { lt: dateRange.end },
          },
        ],
      },
      siteId,
    },
  };

  const defaultWholesaleQueryOptions = {
    variables: {
      filter: {
        ...defaultQueryOptions.variables.filter,
        market: {
          neq: 'unspecified',
        },
      },
      siteId,
    },
  };

  const wholesale = tradeTypes.includes(TRADE_TYPE.Wholesale)
    ? await fetchTrades(
        client,
        GET_WHOLESALE_TRADES_DOWNLOAD,
        { ...defaultWholesaleQueryOptions },
        MarketKey.Wholesale,
      )
    : [];

  const ancillary = tradeTypes.includes(TRADE_TYPE.Ancillary)
    ? await fetchTrades(
        client,
        GET_ANCILLARY_TRADES_DOWNLOAD,
        { ...defaultQueryOptions },
        MarketKey.Ancillary,
      )
    : [];

  const balancingMechanism = tradeTypes.includes(TRADE_TYPE.BalancingMechanism)
    ? await fetchTrades(
        client,
        GET_BALANCING_MECHANISM_TRADES_DOWNLOAD,
        { ...defaultQueryOptions },
        MarketKey.BalancingMechanism,
      )
    : [];

  const data = [...ancillary, ...wholesale, ...balancingMechanism];
  await downloadExcelExport(data, dateRange, tradeTypes, siteName ?? '');

  return data.length > 0;
};
